<template>
  <v-app-bar color="primary" flat :height="72">
    <v-container class="d-flex align-center flex-wrap">
      <!-- SVG logo (component) -->
      <Logo v-if="isLogoComponent" class="logo ml-auto ml-sm-0" height="40px" />

      <!-- PNG logo (string URL) -->
      <img
        v-else-if="logo"
        :src="logo"
        alt="Logo"
        class="logo ml-auto ml-sm-0"
        :height="logoHeight"
      />

      <!-- No logo at all (generic theme before branding loads) -->
      <span v-else class="logo ml-auto ml-sm-0">{{
        brand.info.companyName
      }}</span>
      <destination-filters-toggle
        v-if="$route.matched.some(({ name }) => name === 'destination')"
        class="header-filter"
      ></destination-filters-toggle>
      <details-filters-toggle
        v-if="$route.matched.some(({ name }) => name === 'details')"
      ></details-filters-toggle>
      <v-btn icon class="ml-1 btn-case d-none" color="light">
        <v-icon>mdi-briefcase-outline</v-icon>
      </v-btn>
      <!--
      <mq-layout mq="md+" class="d-none align-center">
        <v-btn variant="text"  @click="isAuth = !isAuth">
          Retrieve booking
        </v-btn>
        <build-itinerary-access v-if="isPlusAccess"></build-itinerary-access>
        <build-itinerary v-else></build-itinerary>
        <profile-menu v-if="isAuth"></profile-menu>
        <sign-up-dialog v-else></sign-up-dialog>
      </mq-layout>
      <mq-layout mq="sm" class="d-none">
        <v-menu
          location="bottom end"
          content-class="header__menu-dropdown header__menu-dropdown-mobile"
          min-width="180"
          v-model="headerMenu"
        >
          <template v-slot:activator="{ props }">
            <v-btn color="light" icon v-bind="props">
              <v-icon>mdi-menu</v-icon>
            </v-btn>
          </template>
          <div class="header__menu-dropdown--wrapper">
            <v-btn class="close-btn" color="light" icon>
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-list width="100%">
              <v-list-item @click="isAuth = !isAuth">
                <v-list-item-title>Retrieve booking</v-list-item-title>
              </v-list-item>
              <build-itinerary-access
                v-if="isPlusAccess"
              ></build-itinerary-access>
              <build-itinerary v-else></build-itinerary>
              <profile-menu v-if="isAuth"></profile-menu>
            </v-list>
            <sign-up-dialog v-if="!isAuth"></sign-up-dialog>
          </div>
        </v-menu>
      </mq-layout>
      -->
    </v-container>
  </v-app-bar>
</template>
<script>
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';
// import SignUpDialog from './panels/SignUpDialog';
// import ProfileMenu from './panels/ProfileMenu';
// import BuildItinerary from '@/components/itinerary/BuildItinerary';
// import BuildItineraryAccess from '@/components/itinerary/BuildItineraryAccess';
import { theme } from '@/utils/theme';

/*
 * A packaged theme may ship its logo as an SVG component; anything else (a URL
 * from the package, a URL from Airtable, or nothing) renders through the <img>
 * / text branches. Register a no-op so the template can always reference <Logo>.
 */
const Logo =
  theme.logo && typeof theme.logo !== 'string'
    ? theme.logo
    : { name: 'LogoPlaceholder', render: () => null };

export default {
  name: 'AppBar',
  components: {
    DestinationFiltersToggle: defineAsyncComponent(() =>
      import('@/components/destination/DestinationFiltersToggle')
    ),
    DetailsFiltersToggle: defineAsyncComponent(() =>
      import('@/components/details/DetailsFiltersToggle')
    ),
    // SignUpDialog,
    // ProfileMenu,
    // BuildItinerary,
    // BuildItineraryAccess,
    Logo
  },
  data: () => ({
    headerMenu: false
  }),
  computed: {
    ...mapGetters('user', ['isAuth', 'isPlusAccess']),
    ...mapGetters('branding', ['brand']),
    // Reactive: swaps from the static theme to the Airtable logo once fetched.
    logo() {
      return this.brand.logo;
    },
    isLogoComponent() {
      return !!this.logo && typeof this.logo !== 'string';
    },
    /*
     * v2 compiled an SVG theme logo into a component and drew it at 40px; the
     * Vue 3 build resolves the same import to a URL, so it lands on the <img>
     * branch instead. At 60px it made the mobile app bar 28px taller than the
     * space `.v-main` reserves for it, which hid the text underneath. Raster
     * logos (meon) still want 60.
     */
    logoHeight() {
      return typeof this.logo === 'string' && this.logo.endsWith('.svg')
        ? 40
        : 60;
    }
  }
};
</script>
<style lang="scss">
@import 'src/scss/utils/__utils';
.v-application {
  .v-app-bar {
    &.v-toolbar {
      .v-toolbar__content {
        padding: 0;
      }
    }
    .logo {
      color: #fff;
      font-weight: bold;
      font-size: 24px;
      text-transform: uppercase;
    }
    @media (min-width: 768px) {
      .btn-case {
        order: 2;
      }
      .logo {
        height: 48px;
        line-height: 48px;
      }
      .header-filter {
        margin-left: auto;
      }
    }
    @media (min-width: 992px) {
      .header-filter {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
      }
    }
    @media (max-width: 767px) {
      .logo {
        color: $primary;
        /*
         * The search pill wraps onto its own row beneath, leaving the logo
         * alone on the first row; centred reads better than pinned right.
         * !important because ml-auto / ml-sm-0 are !important utilities.
         */
        margin-left: auto !important;
        margin-right: auto !important;
      }
      /*
       * The mobile header heights ($header-height-m, $header-height-m-details)
       * were tuned for a 40px logo. A raster logo drawn at its desktop 60px
       * makes the bar ~20px taller than the space .v-main reserves, so the
       * breadcrumb and the search panel end up tucked underneath it.
       */
      img.logo {
        height: 40px;
        width: auto;
      }
      .header-filter {
        order: 3;
        margin-top: 8px;
        width: 100%;
      }
      &.v-toolbar {
        border-bottom: 1px solid $border !important;
        background-color: $primary !important;
        min-height: $header-height-m;
        height: auto !important;
        /*
         * Vuetify 3 pins the app bar at an inline z-index of 1004. The mobile
         * search panel (.destination-filter, z 10) and the details filter
         * sheet (.filter-panel-wrapper, z 10) are designed to slide OVER the
         * lower part of this bar — that is how the destination field stays
         * reachable. Vuetify 2 let them; the inline 1004 painted the bar on
         * top and hid the field. !important is the only thing that beats an
         * inline style.
         */
        z-index: 10 !important;
        .v-toolbar__content {
          height: auto !important;
        }
        .container {
          min-height: $header-height-m;
        }

        .page-details & {
          min-height: $header-height-m-details;

          .container {
            min-height: $header-height-m-details;
          }
        }
      }
    }
    @include queryMax(374px) {
      .header-filter {
        width: calc(100% + 24px);
        border-bottom: 0;
        margin: 12px -12px -12px;
      }
    }
  }

  .filter-toggle {
    cursor: pointer;
    align-items: center;
    position: relative;
    transition: opacity 0.3s;
    background-color: #fff;
    padding: 0 40px 0 24px;
    font-size: 14px;
    line-height: 24px;
    font-weight: 600 !important;
    border-radius: 24px;
    border: 1px solid $border;
    color: $text;
    width: auto;
    display: inline-flex;
    min-height: 48px;

    .v-btn {
      position: absolute;
      right: 7px;
      top: 7px;
    }

    &.filter-hide {
      opacity: 0;
    }

    &:last-child {
      margin-top: 32px;
    }

    &.show {
      display: inline-flex;
    }

    &:not(.show) {
      display: none;
    }

    > span {
      margin: 0 8px;

      &:first-child {
        margin-left: 0;
      }

      &:last-child {
        margin-right: 0;
      }

      &.divider {
        border-right: 1px solid $border;
        height: 24px;
      }
    }

    &:focus {
      outline: none;
    }

    @media (max-width: 767px) {
      width: 100%;
      justify-content: center;
      padding-left: 16px;

      &:last-child {
        margin-top: 16px;
      }

      > span {
        &:not(.divider) {
          white-space: nowrap;
          min-width: 1px;
        }
      }
    }

    @include queryMax(374px) {
      border-width: 1px 0;
      border-radius: 0;
      width: calc(100% + 24px);
      margin: auto -12px 0;
      padding-left: 12px;
      justify-content: space-between;
    }
  }
}
</style>
